import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Shield, Eye, Database, Lock, Mail, Calendar } from 'lucide-react';
import { getCanonicalUrl } from '../utils/seoUtils';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-900 via-blue-800 to-blue-900 py-12">
      <Helmet>
        <title>Privacy Policy - Clock Online</title>
        <meta name="description" content="Clock Online Privacy Policy. Learn how we collect, use, and protect your information. Your privacy is important to us. Read our complete privacy guidelines." />
        <link rel="canonical" href={getCanonicalUrl('/privacy-policy')} />
        <meta property="og:title" content="Privacy Policy - Clock Online" />
        <meta property="og:description" content="Clock Online Privacy Policy. Learn how we collect, use, and protect your information." />
        <meta property="og:url" content={getCanonicalUrl('/privacy-policy')} />
        <meta name="twitter:title" content="Privacy Policy - Clock Online" />
        <meta name="twitter:description" content="Clock Online Privacy Policy. Learn how we collect, use, and protect your information." />
      </Helmet>
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-blue-600/30 p-8 shadow-2xl">
          
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Shield className="w-8 h-8 text-cyan-400" />
              <h1 className="text-3xl font-bold text-white">Privacy Policy</h1>
            </div>
            <p className="text-white/70 text-lg">
              Your privacy is important to us. This policy explains how we collect, use, and protect your information.
            </p>
            <p className="text-white/50 text-sm mt-2">
              Last updated: {new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>

          <div className="space-y-8">
            
            {/* Information Collection */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <Eye className="w-6 h-6 text-cyan-400" />
                <h2 className="text-xl font-semibold text-white">Information We Collect</h2>
              </div>
              <div className="text-white/80 space-y-3">
                <p>
                  Clock Online is designed with privacy in mind. We collect minimal information to provide our services:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Local storage data for your alarm settings, timer preferences, and customizations</li>
                  <li>Browser information for compatibility and performance optimization</li>
                  <li>Usage analytics to improve our service (anonymized and aggregated)</li>
                  <li>No personal identification information is collected or stored on our servers</li>
                </ul>
              </div>
            </section>

            {/* Data Storage */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <Database className="w-6 h-6 text-cyan-400" />
                <h2 className="text-xl font-semibold text-white">How We Store Your Data</h2>
              </div>
              <div className="text-white/80 space-y-3">
                <p>
                  Your data privacy is our priority:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>All your settings are stored locally in your browser's storage</li>
                  <li>No personal data is transmitted to or stored on our servers</li>
                  <li>Your alarm times, timer settings, and preferences remain on your device</li>
                  <li>You have full control over your data and can clear it at any time</li>
                </ul>
              </div>
            </section>

            {/* Third-party Services */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <Lock className="w-6 h-6 text-cyan-400" />
                <h2 className="text-xl font-semibold text-white">Third-party Services</h2>
              </div>
              <div className="text-white/80 space-y-3">
                <p>
                  Clock Online may use the following third-party services:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Web hosting services for application delivery</li>
                  <li>Content delivery networks (CDN) for improved performance</li>
                  <li>Analytics services for usage statistics (anonymized)</li>
                  <li>These services do not have access to your personal alarm or timer data</li>
                </ul>
              </div>
            </section>

            {/* Data Security */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-6 h-6 text-cyan-400" />
                <h2 className="text-xl font-semibold text-white">Data Security</h2>
              </div>
              <div className="text-white/80 space-y-3">
                <p>
                  We implement security measures to protect your information:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>HTTPS encryption for all data transmission</li>
                  <li>Secure hosting infrastructure</li>
                  <li>Regular security updates and monitoring</li>
                  <li>No sensitive personal information is collected or stored</li>
                </ul>
              </div>
            </section>

            {/* Contact Information */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <Mail className="w-6 h-6 text-cyan-400" />
                <h2 className="text-xl font-semibold text-white">Contact Us</h2>
              </div>
              <div className="text-white/80 space-y-3">
                <p>
                  If you have any questions about this Privacy Policy, please contact us:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Email: <a href="mailto:privacy@clockonline.com" className="text-cyan-400 hover:text-cyan-300">privacy@clockonline.com</a></li>
                  <li>General inquiries: <a href="mailto:support@clockonline.com" className="text-cyan-400 hover:text-cyan-300">support@clockonline.com</a></li>
                </ul>
              </div>
            </section>

            {/* Policy Updates */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <Calendar className="w-6 h-6 text-cyan-400" />
                <h2 className="text-xl font-semibold text-white">Policy Updates</h2>
              </div>
              <div className="text-white/80 space-y-3">
                <p>
                  We may update this Privacy Policy from time to time. When we do:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>We will update the "Last updated" date at the top of this policy</li>
                  <li>Significant changes will be highlighted on our website</li>
                  <li>Your continued use of Clock Online constitutes acceptance of any updates</li>
                  <li>We encourage you to review this policy periodically</li>
                </ul>
              </div>
            </section>

          </div>

          {/* Footer */}
          <div className="mt-12 pt-8 border-t border-blue-600/30">
            <div className="text-center">
              <p className="text-white/60 text-sm">
                This Privacy Policy is effective as of the date listed above and applies to all users of Clock Online.
              </p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}